/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this.highVariant = document.getElementsByTagName('high-variant').length > 0;
    this.section = this.closest('.js-product');
    this.productForm = this.section.querySelector('.js-product-form');
    this.optionSelectors = this.querySelectorAll(
      '.option-selector:not(.option-selector--custom)'
    );
    this.colorOptionPosition = this.querySelector(
      '[data-option="color"].option-selector:not(.option-selector--custom)'
    )?.getAttribute('data-option-position');
    this.nativeSelector = document.getElementById(
      `variants-${this.section.dataset.section}`
    );
    this.markersLegends = document.querySelectorAll(
      '.opt-label__markers-legend'
    );
    this.familyPicker = document.querySelector('family-picker');
    this.init();
  }

  async init() {
    this.data = await this.getVariantData();
    this.selectedOptions = this.getSelectedOptions();
    this.variant = this.getSelectedVariant();
    this.markersCount = 0;
    this.updateGiftCardForm();
    // F2RSHOP-520: Enable availability updates on initial load for high-variant products to properly hide OOS colors
    // if (!this.highVariant) {
    this.updateAvailability(null, true);
    // }
    this.updateAddToCartButton();
    this.addEventListener('change', this.handleVariantChange.bind(this));
    this.applySearchParams();
    this.updateVariantsBasedonCookie();

    const currentVariantID = window.location.search.split('variant=')[1];

    if (this.data.variants) {
      this.resetBadges();
      this.badgesLogic(
        false,
        this.data.variants.filter(
          (variant) => variant && variant.id === Number(currentVariantID)
        )[0]
      );
    }

    if (this.highVariant) {
      this.updateHighVariantSwatchImages();
    }

    this.setAttribute('loaded', '');
  }

  /**
   * Handles 'change' events on the variant picker element.
   * @param {object} evt - Event object.
   */
  async handleVariantChange(evt) {
    this.selectedOptions = this.getSelectedOptions();
    this.variant = this.getSelectedVariant();
    this.resetBadges();

    if (this.data.hasOwnProperty('family_products')) {
      this.badgesLogic(true);
    } else {
      this.badgesLogic();
    }

    this.preSelection =
      !this.variant && this.selectedOptions.find((o) => o === null) === null;

    if (this.highVariant) {
      await this.updateAvailability(evt);
      this.updateHighVariantSwatchImages();
    }

    this.updateUrl(evt);
    this.updateVariantInput();
    this.updateAddToCartButton();
    if (!this.highVariant) {
      this.updateAvailability();
    }
    this.updatePrice();
    this.updateBackorderText();
    this.updatePickupAvailability();
    this.updateSku();
    this.updateBarcode();
    this.updateGiftCardForm();
    this.updateModelInfo();
    this.updateCookies();
    VariantPicker.updateLabelText(evt);
    !this.variant && this.updateExistedOption(evt);

    this.dispatchEvent(
      new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions,
        },
      })
    );
  }

  badgesLogic(isFamily = false, variant = this.variant) {
    const currentOptionsWithoutColor = variant?.options
      .filter((option, index) => index !== this.colorOptionPosition - 1)
      .join(' / ');
    const familySwatches = document.querySelectorAll(
      'family-picker .js-option-color'
    );
    this.markersCount = 0;

    if (isFamily) {
      familySwatches?.forEach((familySwatch) => {
        const familyProductId = familySwatch.dataset.productId;
        const familyProductColorPosition = familySwatch.dataset.colorPosition;

        this.showBadges(
          currentOptionsWithoutColor,
          this.data.family_products[familyProductId],
          familyProductColorPosition
        );
      });
    } else {
      this.showBadges(currentOptionsWithoutColor);
    }

    this.toggleMarkersLegend();
  }

  showBadges(
    currentOptionsWithoutColor,
    primaryData = this.data,
    colorPosition = this.colorOptionPosition - 1
  ) {
    const specialVariants = primaryData.variants.filter(
      (variant) =>
        variant.title
          .replaceAll(` / ${variant.options[colorPosition]}`, '')
          .replaceAll(`${variant.options[colorPosition]} / `, '')
          .trim() === currentOptionsWithoutColor
    );

    specialVariants.map((variant) => {
      const colorMarkerValue = primaryData.formatted[variant.id]?.colorMarkers;
      const colorVariantInventory = primaryData.formatted[variant.id]?.inventory;
      const colorSwatch = document.querySelector(
        `[value="${variant.options[colorPosition]}"]`
      );

      if (colorMarkerValue) {
        this.markersCount++;
        const saleMarker = colorSwatch?.nextElementSibling?.querySelector(
          '.opt-label__marker--sale'
        );
        const limitedMarker = colorSwatch?.nextElementSibling?.querySelector(
          '.opt-label__marker--limited'
        );

        if (
          colorMarkerValue.includes(window.theme.strings.colorMarkers.sale) &&
          saleMarker
        ) {
          // Show sale legend
          document
            .querySelector(
              '.opt-label__marker-legend:has(.opt-label__marker--sale)'
            )
            ?.classList.remove('hidden');
          saleMarker.classList.add('is-active');
        }
        if (
          colorMarkerValue.includes(
            window.theme.strings.colorMarkers.limited
          ) &&
          limitedMarker
        ) {
          // Show limited legend
          document
            .querySelector(
              '.opt-label__marker-legend:has(.opt-label__marker--limited)'
            )
            ?.classList.remove('hidden');
          limitedMarker.classList.add('is-active');
        }
      }

      // Cross out the color swatch if the variant is not available
      // if (this.data.hasOwnProperty('family_products')) {
      //   if (colorVariantInventory === 'none') {
      //     colorSwatch.classList.add('is-notavailable');
      //   } else {
      //     colorSwatch.classList.remove('is-notavailable');
      //   }
      // }
    });
  }

  toggleMarkersLegend() {
    this.markersLegends.forEach((legend) => {
      legend.classList.toggle('hidden', this.markersCount === 0);
    });
  }

  resetBadges() {
    const colorMarkers = document.querySelectorAll(
      '.opt-label__marker.is-active'
    );

    colorMarkers.forEach((marker) => {
      marker.classList.remove('is-active');
    });

    // Hide all legend items
    document.querySelectorAll('.opt-label__marker-legend').forEach((legend) => {
      legend.classList.add('hidden');
    });
  }

  /**
   * Updates the "Add to Cart" button label and status.
   */
  updateModelInfo() {
    if (!this.variant) return;

      const { modelInfo } = this.data.formatted[this.variant.id] || {};
    const modelInfoContainer = document.querySelector('.model-information');
    const modelInfoDescr = document.querySelector(
      '.model-information__description'
    );

    if (modelInfoContainer) {
      modelInfoContainer.classList.toggle('active', !!modelInfo);
      if (modelInfoDescr) {
        modelInfoDescr.textContent = modelInfo ? modelInfo : '';
      }
    }
  }

  updateExistedOption(evt) {
    const selectedNonexistentOption = this.querySelector(
      `input[type="radio"]:not([name="${evt.target.name}"]):checked`
    );

    if (!selectedNonexistentOption) return;

    const optionsWrapper = selectedNonexistentOption.parentElement;

    optionsWrapper
      .querySelector('input[type="radio"]:not(.is-nonexistent)')
      .click();
  }

  updateGiftCardForm() {
    const isGiftCard = this.section.classList.contains('gift-card-product');
    if (!isGiftCard) return;
    const recipientForm = document.querySelector('gift-card-recipient');
    const recipientFormCheckbox = recipientForm.querySelector(
      '.gift-card-recipient__checkbox'
    );
    let activeGiftCardType = this.variant.option1;
    const params = new URL(document.location).searchParams;
    const variantIdFromSearchParams = params.get('variant');

    if (variantIdFromSearchParams) {
      const activeVariant = this.data.variants.find(
        (variant) => variant.id === Number(variantIdFromSearchParams)
      );
      activeGiftCardType = activeVariant.option1;
    }
    const isVirtualGiftCard =
      activeGiftCardType === 'Virtual' && !recipientFormCheckbox.checked;
    const isRegularGiftCard =
      activeGiftCardType === 'Regular' && recipientFormCheckbox.checked;

    isVirtualGiftCard && recipientFormCheckbox.click();
    isRegularGiftCard && recipientFormCheckbox.click();
  }

  setKlaviyoCTA() {
    this.addBtn.insertAdjacentHTML(
      'afterend',
      `
      <a href="#"
        class="btn klaviyo-bis-trigger"
        style="text-align: center; margin: 0px; width: auto;"
        data-uw-rm-brl="PR">
        Sold Out - Notifiy Me When It's Available
      </a>
    `
    );
  }

  getKlaviyoCTA() {
    const $klaviyoBISTrigger = this.addBtn?.parentElement?.querySelector(
      '.klaviyo-bis-trigger'
    );

    return $klaviyoBISTrigger;
  }

  atcUnavailable() {
    const unavailableStr = this.variant
      ? theme.strings.noStock
      : theme.strings.noVariant;
    const $klaviyoBISTrigger = this.getKlaviyoCTA();

    // Clear any pending timeouts from previous calls
    this.clearAllTimeouts();

    if (theme.template.includes('product')) {
      // Use 0ms delay for high-variant (immediate), 200ms for regular products
      const hideDelay = this.highVariant ? 0 : 200;
      const klaviyoDelay = this.highVariant ? 0 : 600;

      if (this.variant) {
        if (!$klaviyoBISTrigger) this.setKlaviyoCTA();

        this._atcTimeoutId = setTimeout(() => {
          this.addBtn.classList.remove('atc-visible');
          this.addBtn.classList.add('atc-hidden');
          this._atcTimeoutId = null;
        }, hideDelay);

        this._klaviyoTimeoutId = setTimeout(() => {
          const klaviyoTrigger = this.addBtn.parentElement.querySelector('.klaviyo-bis-trigger');
          if (klaviyoTrigger) {
            klaviyoTrigger.style.display = 'block';
          }
          this._klaviyoTimeoutId = null;
        }, klaviyoDelay);
      } else {
        if ($klaviyoBISTrigger) $klaviyoBISTrigger.remove();

        this._atcTimeoutId = setTimeout(() => {
          this.addBtn.classList.remove('atc-hidden');
          this.addBtn.classList.add('atc-visible');
          this._atcTimeoutId = null;
        }, hideDelay);
      }
    }

    this.addBtn.textContent = unavailableStr;
    this.addBtn.disabled = true;
  }

  atcAvailable() {
    const $klaviyoBISTrigger = this.getKlaviyoCTA();

    // Clear any pending timeouts from atcUnavailable
    this.clearAllTimeouts();

    if ($klaviyoBISTrigger) $klaviyoBISTrigger.remove();

    // Show button using CSS class
    this.addBtn.classList.remove('atc-hidden');
    this.addBtn.classList.add('atc-visible');

    this.addBtn.textContent = this.addBtn.dataset.addToCartText;
    this.addBtn.disabled = false;
  }

  /**
   * Clears all pending timeouts for ATC button management.
   */
  clearAllTimeouts() {
    if (this._atcTimeoutId) {
      clearTimeout(this._atcTimeoutId);
      this._atcTimeoutId = null;
    }
    if (this._klaviyoTimeoutId) {
      clearTimeout(this._klaviyoTimeoutId);
      this._klaviyoTimeoutId = null;
    }
  }

  updateAddToCartButton() {
    if (!this.productForm) return;
    if (this.selectedOptions.indexOf(null) > -1) return;

    this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');

    const variantAvailable =
      this.variant &&
      this.variant.available &&
      this.data.formatted[this.variant.id]?.disable_online != 'true';

    variantAvailable ? this.atcAvailable() : this.atcUnavailable();
  }

  /**
   * Updates the availability status of an option.
   * @param {Element} optionEl - Option element.
   * @param {boolean} exists - Does this option lead to a variant that exists?
   * @param {boolean} available - Does this option lead to a variant that is available to buy?
   */
  static updateOptionAvailability(
    data,
    optionEl,
    exists,
    available,
    initialPageLoad
  ) {
    const highVariant =
      document.getElementsByTagName('high-variant').length > 0;
    // F2RSHOP-520: Enable hiding OOS colors for high-variant products same as for regular ones
    // if (highVariant) {
    //   return;
    // }
    const el = optionEl;
    const selectedOptions = Array.from(
      el.closest('.js-product').querySelectorAll('.js-option:checked')
    ).map((selector) => selector.value || null);

    const unavailableText = exists
      ? theme.strings.noStock
      : theme.strings.noVariant;

    el.classList.toggle('is-unavailable', !available);
    el.classList.toggle('is-nonexistent', !exists);

    el.classList.remove('is-hidden');

    if (optionEl.classList.contains('custom-select__option')) {
      const em = el.querySelector('em');

      if (em) {
        em.hidden = available;
      }

      if (!available) {
        if (em) {
          em.textContent = unavailableText;
        } else {
          el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
        }
      }
    } else if (!available) {
      el.nextElementSibling.title = unavailableText;
    } else {
      el.nextElementSibling.removeAttribute('title');
    }

    // Additional logic to check color variants
    if (el.classList.contains('js-option-color') && !initialPageLoad == true) {
      const colorIndex =
        parseInt(
          el.closest('[data-option-position]').dataset.optionPosition,
          10
        ) - 1;

      const isColorAvailable = data.variants.some((variant) => {
        return (
          variant.options.every((option, index) => {
            if (index === colorIndex) {
              return option === el.value;
            }

            return selectedOptions[index] === option;
          }) && VariantPicker.isVariantPurchasable(data, variant)
        );
      });

      el.classList.toggle('is-notavailable', !isColorAvailable);
    }
  }

  static isVariantPurchasable(data, variant) {
    const disableOnline = data?.formatted?.[variant?.id]?.disable_online;

    return Boolean(
      variant?.available &&
        disableOnline !== true &&
        disableOnline !== 'true'
    );
  }

  /**
   * Updates the availability status in option selectors.
   */
  updateAvailability(evt, initialPageLoad) {
    if (this.dataset.showAvailability === 'false') return;
    const { availabilityMode } = this.dataset; // 'down' or 'selection'
    let currVariant = this.variant;

    if (!this.variant) {
      currVariant = { options: this.selectedOptions };
    }

    if (availabilityMode === 'selection') {
      // Flag all options as unavailable
      this.querySelectorAll('.js-option').forEach((optionEl) => {
        VariantPicker.updateOptionAvailability(
          this.data,
          optionEl,
          false,
          false,
          initialPageLoad
        );
      });

      // Flag selector options as available or sold out, depending on the variant availability
      this.optionSelectors.forEach((selector, selectorIndex) => {
        this.data.variants.forEach((variant) => {
          let matchCount = 0;

          variant.options.forEach((option, optionIndex) => {
            if (
              option === currVariant.options[optionIndex] &&
              optionIndex !== selectorIndex
            ) {
              matchCount += 1;
            }
          });

          if (matchCount === currVariant.options.length - 1) {
            const options = selector.querySelectorAll('.js-option');
            const optionEl = Array.from(options).find((opt) => {
              if (selector.dataset.selectorType === 'dropdown') {
                return opt.dataset.value === variant.options[selectorIndex];
              }
              return opt.value === variant.options[selectorIndex];
            });

            if (optionEl) {
              VariantPicker.updateOptionAvailability(
                this.data,
                optionEl,
                true,
                VariantPicker.isVariantPurchasable(this.data, variant),
                initialPageLoad
              );
            }
          }
        });
      });
    } else {
      this.optionSelectors.forEach((selector, selectorIndex) => {
        const options = selector.querySelectorAll(
          '.js-option:not([data-value=""])'
        );
        options.forEach((option) => {
          const optionValue =
            selector.dataset.selectorType === 'dropdown'
              ? option.dataset.value
              : option.value;

          // any available variants with previous options and this one locked in?
          let variantsExist = false;
          let variantsAvailable = false;
          let wholeOptionsHidden = false;
          let variantsHidden = [];
          this.data.variants.forEach((v) => {
            if (!v || v === null) {
              return;
            }

            let matches = 0;
            for (let i = 0; i < selectorIndex; i += 1) {
              if (
                v.options[i] === this.selectedOptions[i] ||
                this.selectedOptions[i] === null
              ) {
                matches += 1;
              }
            }
            if (
              v.options[selectorIndex] === optionValue &&
              matches === selectorIndex
            ) {
              variantsExist = true;
              if (VariantPicker.isVariantPurchasable(this.data, v)) {
                variantsAvailable = true;
              }

              if (this.data.formatted[v.id]?.disable_online != 'true') {
                variantsHidden.push('false');
              } else {
                variantsHidden.push('true');
              }
            }
          });

          if (variantsHidden.length > 0) {
            if (
              variantsHidden.every((variantHidden) => variantHidden === 'true')
            ) {
              wholeOptionsHidden = true;
            }
          }
          VariantPicker.updateOptionAvailability(
            this.data,
            option,
            variantsExist,
            variantsAvailable,
            initialPageLoad
          );
        });
      });
    }

    return;

    /**
     * F2RSHOP-520: Add early-return and commented out fetch and DOM manipulation
     * to align high-variant rendering logic with regular ones.
     * - Seems like fetch below is redundant, as it incorrectly resets the selected
     * option states (may cause resetting to the first color option, even if it's
     * unavailable) and re-renders the HTML with unavailable colors fully visible
     */

    // if (!this.highVariant) {
    //   return;
    // }

    // const sectionId = this.dataset.sectionId;
    // const selectedOptionValues = Array.from(
    //   this.querySelectorAll('input[type="radio"]:checked')
    // ).map(({ dataset }) => dataset.optionValueId);

    // const params =
    //   selectedOptionValues.length > 0
    //     ? `&option_values=${selectedOptionValues.join(',')}`
    //     : '';

    // const toggleElementState = (elements, disabled) => {
    //   elements.forEach((el) => {
    //     if (!el) return;
    //     el.style.transition = disabled ? 'opacity 0s' : '';
    //     el.style.opacity = disabled ? '0.5' : '1';
    //     el.style.pointerEvents = disabled ? 'none' : '';
    //   });
    // };

    // const sizeContainer = document.querySelector('[data-option="size"] > div');
    // const addToCartContainer = document.querySelector(
    //   '.quantity-submit-row__submit'
    // );
    // const buyWithShopContainer = document.querySelector(
    //   '.shopify-payment-button'
    // );

    // toggleElementState(
    //   [sizeContainer, addToCartContainer, buyWithShopContainer],
    //   true
    // );

    // let fetchUrl = `${window.location.href}`;
    // if (fetchUrl.indexOf('?') === -1) {
    //   fetchUrl = fetchUrl + '?';
    // }
    // fetchUrl = fetchUrl + `${params}`;

    // if (fetchUrl.indexOf('variant=') === -1) {
    //   fetchUrl =
    //     fetchUrl +
    //     '&variant=' +
    //     document.querySelector('input[name="id"]').value;
    // }

    // return fetch(fetchUrl)
    //   .then((response) => response.text())
    //   .then((responseText) => {
    //     const html = new DOMParser().parseFromString(responseText, 'text/html');

    //     selectedOptionValues.forEach((selectedOption) => {
    //       const optionElement = html.querySelector(
    //         `[data-option-value-id="${selectedOption}"]`
    //       );
    //       if (optionElement) optionElement.checked = true;
    //     });

    //     const updateIfExists = (selector) => {
    //       const el = document.querySelector(selector);
    //       const newEl = html.querySelector(selector);
    //       if (el && newEl) el.innerHTML = newEl.innerHTML;
    //     };

    //     [
    //       '.price-container',
    //       '[data-option="size"]',
    //       '[data-option="color"]',
    //       '[data-option="width"]',
    //       '#variants-data',
    //     ].forEach(updateIfExists);

    //     if (
    //       document.querySelectorAll('input[type="radio"]:checked').length !=
    //       selectedOptionValues.length
    //     ) {
    //       document.querySelectorAll(
    //         'input[type="radio"]:checked'
    //       )[0].checked = false;
    //     }

    //     this.data = this.getVariantData();
    //   })
    //   .finally(() => {
    //     toggleElementState([addToCartContainer, buyWithShopContainer], false);
    //   });
  }

  /**
   * Updates the backorder text and visibility.
   */
  updateBackorderText() {
    this.backorder = this.backorder || this.section.querySelector('.backorder');
    if (!this.backorder) return;

    let hideBackorder = true;

    if (this.variant && this.variant.available) {
      const { inventory } = this.data.formatted[this.variant.id];

      if (this.variant.inventory_management && inventory === 'none') {
        const backorderProdEl = this.backorder.querySelector(
          '.backorder__product'
        );
        const prodTitleEl = this.section.querySelector('.product-title');
        const variantTitle = this.variant.title.includes('Default')
          ? ''
          : ` - ${this.variant.title}`;

        backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
        hideBackorder = false;
      }
    }

    this.backorder.hidden = hideBackorder;
  }

  /**
   * Updates the colour option label text.
   * @param {object} evt - Event object
   */
  static updateLabelText(evt) {
    if (!evt.target) return;

    const selector = evt.target.closest('.option-selector');

    if (!selector || selector.dataset.selectorType === 'dropdown') return;

    const colorText = selector.querySelector('.js-color-text');
    const widthText = selector.querySelector('.js-width-text');
    const sizeText = selector.querySelector('.js-size-text');
    const fullName = evt.target.nextElementSibling
      .querySelector('.js-value')
      .getAttribute('data-full-name');

    const valueText = fullName
      ? fullName
      : evt.target.nextElementSibling.querySelector('.js-value').textContent;

    if (colorText) {
      colorText.textContent = valueText;
    } else if (widthText) {
      widthText.textContent = valueText;
    } else if (sizeText) {
      sizeText.textContent = valueText;
    }
  }

  /**
   * Updates the pick up availability.
   */
  updatePickupAvailability() {
    customElements.whenDefined('pickup-availability').then(() => {
      this.pickUpAvailability =
        this.pickUpAvailability ||
        this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability?.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    });
  }

  /**
   * Updates the price.
   */
  updatePrice() {
    this.price =
      this.price || this.section.querySelector('.product-price .price');
    this.clubPrice =
      this.clubPrice ||
      this.section.querySelector('.product-price .price--club-pdp');
    if (!this.price) return;

    let { variant } = this;
    if (this.preSelection) {
      variant = this.data.variants[0];
      for (let i = 1; i < this.data.variants.length; i += 1) {
        if (this.data.variants[i].price < variant.price)
          variant = this.data.variants[i];
      }
    }

    if (variant) {
      const priceCurrentEl = this.price.querySelector('.price__current');
      const priceWasEl = this.price.querySelector('.price__was');
      const unitPriceEl = this.price.querySelector('.unit-price');
      const currentVariantPrice = this.data.variants.find(
        (el) => el.id === variant.id
      ).price;
      // Update current price and original price if on sale.
      priceCurrentEl.innerHTML = this.data.formatted[variant.id]?.price;
      if (this.clubPrice) {
        const variantSale = this.data.formatted[variant.id]?.variantSale;
        const isvariantSale = variantSale === 'Markdown';
        this.clubPrice.classList.toggle('active', !isvariantSale);

        const clubPrice = this.clubPrice.querySelector('.club-price');
        const clubDiscount = 1 - window.theme.settings.clubPriceDiscount / 100;
        const updatedClubPrice = (currentVariantPrice * clubDiscount) / 100;
        const formettedClubPrice = updatedClubPrice.toFixed(2);
        clubPrice.innerHTML = `${window.theme.currencySign}${formettedClubPrice}`;
      }
      if (priceWasEl) {
        const hasComparePrice = variant.compare_at_price > variant.price;

        priceWasEl.innerHTML = hasComparePrice
          ? this.data.formatted[variant.id]?.compareAtPrice || ''
          : '';
      }

      // Update unit price, if specified.
      if (variant.unit_price_measurement) {
        const valueEl = this.price.querySelector('.unit-price__price');
        const unitEl = this.price.querySelector('.unit-price__unit');
        const value = variant.unit_price_measurement.reference_value;
        const unit = variant.unit_price_measurement.reference_unit;

        valueEl.innerHTML = this.data.formatted[variant.id]?.unitPrice || '';
        unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        unitPriceEl.hidden = false;
      } else if (unitPriceEl) {
        unitPriceEl.hidden = true;
      }

      this.price.classList.toggle(
        'price--on-sale',
        variant.compare_at_price > variant.price
      );
      this.price.classList.toggle(
        'price--sold-out',
        !variant.available && !this.preSelection
      );
    }

    this.price.querySelector('.price__default').hidden =
      !this.variant && !this.preSelection;
    this.price.querySelector('.price__no-variant').hidden =
      this.variant || this.preSelection;
    const from = this.price.querySelector('.price__from');
    if (from) {
      from.hidden = !this.preSelection;
    }
  }

  /**
   * Updates the SKU.
   */
  updateSku() {
    this.sku = this.sku || this.section.querySelector('.product-sku__value');
    if (!this.sku) return;

    const skuAvailable = this.variant && this.variant.sku;
    this.sku.textContent = skuAvailable ? this.variant.sku : '';
    this.sku.parentNode.hidden = !skuAvailable;
  }

  /**
   * Updates the Barcode.
   */
  updateBarcode() {
    this.barcode =
      this.barcode || this.section.querySelector('.product-barcode__value');
    if (!this.barcode) return;

    const barcodeAvailable = this.variant && this.variant.barcode;
    this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
    this.barcode.parentNode.hidden = !barcodeAvailable;
  }

  /**
   * Updates the url with the selected variant id.
   * @param {object} evt - Event object.
   */
  updateUrl(evt) {
    if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false')
      return;
    const url = this.variant
      ? `${this.dataset.url}?variant=${this.variant.id}`
      : this.dataset.url;

    window.history.replaceState({}, '', url);
  }

  /**
   * Updates the value of the hidden [name="id"] form inputs.
   */
  updateVariantInput() {
    this.forms =
      this.forms ||
      this.section.querySelectorAll('.js-product-form, .js-instalments-form');

    this.forms.forEach((form) => {
      const input = form.querySelector('[name="id"]');
      input.value = this.variant ? this.variant.id : '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  setCookie(cname, cvalue, exdays) {
    const d = new Date();
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
    let expires = 'expires=' + d.toUTCString();
    document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
  }

  getCookie(cname) {
    let name = cname + '=';
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  updateCookies() {
    var productFamilyNameDiv = document.querySelector('#productFamily-name');
    if (productFamilyNameDiv) {
      var productFamilyName = productFamilyNameDiv.dataset.familyName;
      var productFormId = document.querySelector('#productFamily-name').dataset
        .formId;
      var selectedWidth = 0;
      selectedWidth = document.querySelector(
        'input[name="' + productFormId + '-width-option"]:checked'
      ).value;
      var selectedSize = 0;
      selectedSize = document.querySelector(
        'input[name="' + productFormId + '-size-option"]:checked'
      ).value;
      this.setCookie(productFamilyName, selectedWidth + '|' + selectedSize, 30);
    }
  }

  /**
   * Gets the variant data for a product.
   * @returns {Promise<Object>}
   */
  async getVariantData() {
    const dataEl = this.querySelector('[type="application/json"]');
    let initialData = {};

    try {
      initialData = JSON.parse(
        dataEl.textContent.replace(/},]/g, '}]').replace(/},}/g, '}}')
      );
    } catch (error) {
      console.error('Failed to parse initial data:', error);
    }

    // For high-variant products, fetch all variants via Storefront API
    if (this.highVariant) {
      try {
        const apiData = await this.fetchAllVariantsViaAPI(initialData);

        if (apiData?.variants?.length) {
          return apiData;
        }
      } catch (error) {
        console.error('Failed to fetch all variants via API, using initial data:', error);
      }
    }

    return initialData;
  }

  /**
   * Fetch all variants for a product via Storefront API with pagination
   * @param {Object} initialData - Initial data from Liquid for reference
   * @returns {Promise<Object>} Variants data matching the Liquid structure
   */
  async fetchAllVariantsViaAPI(initialData) {
    const { storefront_api_key, storefront_api_ver } = window.theme?.settings || {};
    if (!storefront_api_key || !storefront_api_ver) {
      console.error('Storefront API credentials not configured');
      return null;
    }

    // Get handle from data attribute (set in Liquid) or fallback to URL parsing
    let handle = this.dataset.productHandle || null;

    if (!handle && this.dataset.url) {
      const urlParts = this.dataset.url.split('/products/');
      handle = urlParts.length > 1 ? urlParts[1].split('?')[0].split('#')[0] : null;
    }

    if (!handle) {
      console.error('Product handle not found');
      return null;
    }

    // Get store URL from data attribute, theme.routes.base, or fallback to current origin
    const storeUrl = this.dataset.storeUrl ||
                     window.theme?.routes?.base ||
                     window.location.origin;
    const apiUrl = `${storeUrl}/api/${storefront_api_ver}/graphql.json`;
    const headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': storefront_api_key,
    };

    // GraphQL query - fetch only needed fields
    const query = `
      query getProductVariants($handle: String!, $cursor: String) {
        product(handle: $handle) {
          variants(first: 250, after: $cursor) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                availableForSale
                price { amount }
                compareAtPrice { amount }
                selectedOptions { value }
                image { id url altText }
                metafields(identifiers: [
                  {namespace: "custom", key: "disable_online"},
                  {namespace: "custom", key: "model_information"},
                  {namespace: "custom", key: "sale"},
                  {namespace: "custom", key: "pdp_color_marker"}
                ]) {
                  namespace
                  key
                  value
                }
              }
            }
          }
        }
      }
    `;

    // Fetch all variants with pagination
    const allVariants = [];
    const formatted = {};
    const initialFormatted = initialData?.formatted || {};
    const initialVariantsById = {};

    initialData?.variants?.forEach((variant) => {
      if (variant?.id) {
        initialVariantsById[variant.id] = variant;
      }
    });

    let cursor = null;
    let hasNextPage = true;

    while (hasNextPage) {
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({ query, variables: { handle, cursor } }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const { data, errors } = await response.json();

        if (errors) {
          console.error('GraphQL errors:', errors);
          break;
        }

        const variants = data?.product?.variants;
        if (!variants) break;

        // Transform and format variants in one pass
        variants.edges?.forEach(({ node }) => {
          const variantId = parseInt(node.id.split('/').pop(), 10);

          // Build variant object
          const variant = {
            id: variantId,
            title: node.title,
            available: node.availableForSale,
            price: Math.round(parseFloat(node.price.amount) * 100),
            compare_at_price: node.compareAtPrice
              ? Math.round(parseFloat(node.compareAtPrice.amount) * 100)
              : null,
            options: node.selectedOptions.map((opt) => opt.value),
            featured_media: null,
          };

          const liquidVariant = initialVariantsById[variantId];

          if (liquidVariant?.featured_media) {
            variant.featured_media = liquidVariant.featured_media;
          } else if (node.image) {
            const imageId = parseInt(node.image.id.split('/').pop(), 10);

            variant.featured_media = {
              id: imageId,
              src: node.image.url,
              alt: node.image.altText || '',
            };
          }

          // Process metafields efficiently
          if (node.metafields?.length) {
            const metafields = {};
            node.metafields.forEach((mf) => {
              if (mf?.namespace === 'custom' && mf?.key) {
                metafields[mf.key] = mf.value;
              }
            });

            if (metafields.disable_online !== undefined) {
              variant.disable_online = metafields.disable_online === 'true';
            }
            if (metafields.model_information) {
              try {
                variant.model_information = JSON.parse(metafields.model_information);
              } catch {
                variant.model_information = metafields.model_information;
              }
            }
            if (metafields.sale) variant.sale = metafields.sale;
            if (metafields.pdp_color_marker) variant.pdp_color_marker = metafields.pdp_color_marker;
          }

          allVariants.push(variant);

          // Format variant data immediately (reuse existing if available)
          const existing = initialFormatted[variantId];
          formatted[variantId] = {
            price: existing?.price || this.formatPrice(variant.price),
            compareAtPrice: variant.compare_at_price
              ? this.formatPrice(variant.compare_at_price)
              : existing?.compareAtPrice || null,
            disable_online: variant.disable_online !== undefined
              ? (variant.disable_online ? 'true' : 'false')
              : (existing?.disable_online || 'false'),
            modelInfo: variant.model_information || existing?.modelInfo || null,
            variantSale: variant.sale || existing?.variantSale || null,
            colorMarkers: variant.pdp_color_marker || existing?.colorMarkers || null,
            inventory: !variant.available ? 'none' : existing?.inventory || null,
          };
        });

        hasNextPage = variants.pageInfo.hasNextPage;
        cursor = variants.pageInfo.endCursor;
      } catch (error) {
        console.error('Error fetching variants:', error);
        break;
      }
    }

    return {
      variants: allVariants,
      formatted
    };
  }

  /**
   * Fill color swatch thumbnails from full variant data when Liquid could not resolve featured_media.
   */
  updateHighVariantSwatchImages() {
    if (!this.colorOptionPosition || !this.data?.variants?.length) {
      return;
    }

    const colorIndex = Number.parseInt(this.colorOptionPosition, 10) - 1;
    const mediaByColor = new Map();

    this.data.variants.forEach((variant) => {
      const color = variant?.options?.[colorIndex];

      if (!color) {
        return;
      }

      const media = variant.featured_media;

      if (!media?.src) {
        return;
      }

      const existing = mediaByColor.get(color);

      if (!existing || (media.id && !existing.id)) {
        mediaByColor.set(color, media);
      }
    });

    this.querySelectorAll('.js-option-color').forEach((input) => {
      if (input.dataset.swatchNeedsImage !== 'true') {
        return;
      }

      const media = mediaByColor.get(input.value);

      if (!media?.src) {
        return;
      }

      const img = input.nextElementSibling?.querySelector('.opt-label__media img');

      if (!img) {
        return;
      }

      img.src = VariantPicker.buildSwatchImageUrl(media.src, 138);
      img.srcset = `${VariantPicker.buildSwatchImageUrl(media.src, 138)} 138w, ${VariantPicker.buildSwatchImageUrl(media.src, 276)} 276w`;
      input.removeAttribute('data-swatch-needs-image');
    });
  }

  /**
   * @param {string} url - Image URL
   * @param {number} width - Requested width
   * @returns {string}
   */
  static buildSwatchImageUrl(url, width) {
    try {
      const parsed = new URL(url, window.location.origin);

      parsed.searchParams.set('width', String(width));

      return parsed.toString();
    } catch {
      return url;
    }
  }

  /**
   * Format price to match Liquid money format
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price string
   */
  formatPrice(priceInCents) {
    const price = priceInCents / 100;
    const format = window.theme?.settings?.moneyWithCurrencyFormat || '{{amount}}';
    // Replace {{amount}} with formatted price (matching shipping-calculator.js pattern)
    return format.replace(/\{\{\s*amount\s*\}\}/, price.toFixed(2));
  }

  /**
   * Get the selected values from a list of variant options.
   * @returns {Array} Array of selected option values, value is null if not selected.
   */
  getSelectedOptions() {
    const selectedOptions = [];

    this.optionSelectors.forEach((selector) => {
      if (selector.dataset.selectorType === 'dropdown') {
        const selected = selector.querySelector(
          '.custom-select__option[aria-selected="true"]'
        );
        selectedOptions.push(
          selected && selected.dataset.value !== ''
            ? selected.dataset.value
            : null
        );
      } else {
        const selected = selector.querySelector('input:checked');

        selectedOptions.push(selected ? selected.value : null);
      }
    });

    return selectedOptions;
  }

  /**
   * Check width and size from cookies and set value if available
   */
  updateVariantsBasedonCookie() {
    var productFamilyNameDiv = document.querySelector('#productFamily-name');
    if (productFamilyNameDiv) {
      var productFamilyName = productFamilyNameDiv.dataset.familyName;
      var productFormId = document.querySelector('#productFamily-name').dataset
        .formId;
      var productCookie = this.getCookie(productFamilyName);
      const allParam = productCookie.split('|');

      this.optionSelectors.forEach((selector) => {
        const mainOption = selector.querySelector('.label').innerHTML;
        if (mainOption == 'Width') {
          const allWidths = selector.querySelectorAll('input');
          allWidths.forEach((curWidth) => {
            if (
              curWidth.value == allParam[0] &&
              !curWidth.classList.contains('is-unavailable')
            ) {
              curWidth.checked = true;
            }
          });
        }
        if (mainOption == 'Size') {
          const allSizes = selector.querySelectorAll('input');
          allSizes.forEach((curSize) => {
            if (
              curSize.value == allParam[1] &&
              !curSize.classList.contains('is-unavailable')
            ) {
              curSize.checked = true;
            }
          });
        }
      });

      this.handleVariantChange(this);
    }
  }

  /**
   * Get selected variant data.
   * @returns {?object} Variant object, or null if one is not selected.
   */
  getSelectedVariant() {
    return this.data.variants.find((v) =>
      v.options.every((val, index) => val === this.selectedOptions[index])
    );
  }

  /**
   * Apply search parameters.
   */
  applySearchParams() {
    const searchParams = new URLSearchParams(window.location.search);

    Array.from(searchParams.keys()).forEach((key) => {
      const value = searchParams.get(key);
      const optionSelectors = Array.from(this.optionSelectors);
      const matchingOptionSelector = optionSelectors.find(
        (x) => x.dataset.option === key
      );
      if (!matchingOptionSelector) return;

      if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
        const colorOptionDropdown = matchingOptionSelector.querySelector(
          `.custom-select__option[data-value="${value}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      } else {
        const matchingInput = matchingOptionSelector.querySelector(
          `input[value="${value}"]`
        );
        if (matchingInput) {
          const matchingOptionValue = matchingOptionSelector.querySelector(
            '.option-selector__label-value'
          );
          if (matchingOptionValue) {
            matchingOptionSelector.querySelector(
              '.option-selector__label-value'
            ).textContent = value;
          }
          matchingInput.checked = true;
          matchingInput.dispatchEvent(
            new CustomEvent('change', { bubbles: true, cancelable: false })
          );
        }
      }
    });
  }
}

customElements.get('variant-picker') ||
  customElements.define('variant-picker', VariantPicker);
